/**
 * =============================================================================
 * Edit App modal (FE issue #25)
 * =============================================================================
 *
 * Until now `AppForm` could only *create* an app, so changing an image
 * reference meant deleting the app and adding it again — which is impossible
 * for any app that was ever played (FE issue #24). The backend has always
 * accepted `PUT /api/v1/apps/{id}`; this is the missing UI.
 *
 * The common case is made one click: four apps are frozen on January images
 * because `docker_image_url` holds a pinned `@sha256:` digest. The modal
 * offers the repository's floating tags from Harbor (`GET /apps/{id}/image-tags`)
 * and, the other way round, pinning the digest that is running right now.
 *
 * Changing the image or the config moves the app back to `pending_approval`
 * on the backend — the modal says so before anything is saved.
 */

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Modal from './Modal'
import {
  appsApi,
  deviceTypesApi,
  type AppDetail,
  type AppUpdate,
} from '../services/api'
import { buildUpdatePayload, payloadNeedsReapproval } from '../utils/appUpdatePayload'
import {
  isValidImageRef,
  branchFromTag,
  isPinnedDigest,
  withReference,
  shortDigest,
} from '../utils/imageRef'
import { at } from '../utils/appAdminI18n'

const CATEGORIES = ['diagnostics', 'rehabilitation', 'cognitive', 'other'] as const

export interface AppEditModalProps {
  isOpen: boolean
  onClose: () => void
  app: AppDetail
  /** Called after a successful save with the app returned by the backend. */
  onSaved?: (app: AppDetail) => void
}

const AppEditModal: React.FC<AppEditModalProps> = ({ isOpen, onClose, app, onSaved }) => {
  const queryClient = useQueryClient()

  const [name, setName] = useState(app.name)
  const [description, setDescription] = useState(app.description || '')
  const [category, setCategory] = useState(app.category || 'rehabilitation')
  const [imageRef, setImageRef] = useState(app.docker_image_url || '')
  const [deviceTypeIds, setDeviceTypeIds] = useState<string[]>(
    app.supported_device_type_ids || [],
  )
  const [configYaml, setConfigYaml] = useState<string | undefined>(undefined)
  const [configName, setConfigName] = useState('')
  const [paramsYaml, setParamsYaml] = useState<string | undefined>(undefined)
  const [paramsName, setParamsName] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Reset the form whenever it is (re)opened for an app.
  useEffect(() => {
    if (!isOpen) return
    setName(app.name)
    setDescription(app.description || '')
    setCategory(app.category || 'rehabilitation')
    setImageRef(app.docker_image_url || '')
    setDeviceTypeIds(app.supported_device_type_ids || [])
    setConfigYaml(undefined)
    setConfigName('')
    setParamsYaml(undefined)
    setParamsName('')
    setSubmitError(null)
  }, [isOpen, app])

  const { data: deviceTypes } = useQuery({
    queryKey: ['device-types'],
    queryFn: () => deviceTypesApi.list(),
    enabled: isOpen,
  })

  const { data: imageTags, isLoading: tagsLoading } = useQuery({
    queryKey: ['app-image-tags', app.id],
    queryFn: () => appsApi.getImageTags(app.id),
    enabled: isOpen,
    retry: false,
  })

  const activeDeviceTypes = useMemo(
    () => (deviceTypes?.data ?? []).filter((t) => t.is_active),
    [deviceTypes],
  )

  const imageValid = isValidImageRef(imageRef)
  const branchHint = branchFromTag(imageRef)
  const pinned = isPinnedDigest(imageRef)
  const tags = imageTags?.data?.tags ?? []
  const harborAvailable = imageTags?.data?.harbor_available !== false
  const runningDigest = app.current_image_digest

  const payload = buildUpdatePayload(app, {
    name,
    description,
    category,
    imageRef,
    deviceTypeIds,
    configYaml,
    appParametersYaml: paramsYaml,
  })
  const hasChanges = Object.keys(payload).length > 0
  const willNeedReapproval = payloadNeedsReapproval(payload)
  const canSubmit =
    name.trim().length > 0 && imageValid && deviceTypeIds.length > 0 && hasChanges

  const updateMutation = useMutation({
    mutationFn: (data: AppUpdate) => appsApi.update(app.id, data),
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ['app', app.id] })
      queryClient.invalidateQueries({ queryKey: ['app-reviews', app.id] })
      queryClient.invalidateQueries({ queryKey: ['app-config', app.id] })
      queryClient.invalidateQueries({ queryKey: ['app-image-tags', app.id] })
      queryClient.invalidateQueries({ queryKey: ['apps'] })
      onSaved?.(resp.data)
      onClose()
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail
      setSubmitError(typeof detail === 'string' ? detail : at('saveFailed'))
    },
  })

  const toggleDeviceType = (id: string) =>
    setDeviceTypeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )

  const readFile = async (
    file: File | undefined,
    setText: (v: string | undefined) => void,
    setLabel: (v: string) => void,
  ) => {
    if (!file) {
      setText(undefined)
      setLabel('')
      return
    }
    setText(await file.text())
    setLabel(file.name)
  }

  const applyReference = (reference: string) => {
    const next = withReference(imageRef, reference)
    if (next) setImageRef(next)
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)
    if (!canSubmit) return
    updateMutation.mutate(payload)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={at('editTitle')} size="lg">
      <form onSubmit={onSubmit} className="space-y-5" aria-label="edit-app-form">
        <p className="text-sm text-gray-600">{at('editIntro')}</p>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="edit-app-name">
            {at('fieldName')} *
          </label>
          <input
            id="edit-app-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="edit-app-desc">
            {at('fieldDescription')}
          </label>
          <textarea
            id="edit-app-desc"
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="edit-app-category">
            {at('fieldCategory')}
          </label>
          <select
            id="edit-app-category"
            className="input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* ---- Image reference: the whole point of issue #25 ---- */}
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="edit-app-image">
            {at('fieldImage')} *
          </label>
          <input
            id="edit-app-image"
            className={`input font-mono text-sm ${imageRef && !imageValid ? 'border-red-500' : ''}`}
            value={imageRef}
            onChange={(e) => setImageRef(e.target.value)}
            aria-invalid={!!imageRef && !imageValid}
          />
          {imageRef && !imageValid && (
            <p className="text-red-600 text-sm mt-1" role="alert">
              {at('invalidImage')}
            </p>
          )}
          {branchHint && (
            <p className="text-teal-700 text-sm mt-1">
              {at('branchLabel')}: {branchHint}
            </p>
          )}

          {pinned && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <h4 className="text-sm font-semibold text-amber-900">{at('pinnedTitle')}</h4>
              <p className="text-sm text-amber-800 mt-1">{at('pinnedBody')}</p>
            </div>
          )}

          <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <h4 className="text-sm font-medium text-gray-800">{at('floatingTagsTitle')}</h4>
            {tagsLoading && <p className="text-sm text-gray-500 mt-1">{at('loadingTags')}</p>}
            {!tagsLoading && (!harborAvailable || tags.length === 0) && (
              <p className="text-sm text-gray-500 mt-1">{at('floatingTagsNone')}</p>
            )}
            {!tagsLoading && tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <button
                    key={tag.name}
                    type="button"
                    className="btn-secondary text-sm"
                    onClick={() => applyReference(tag.name)}
                    title={tag.digest ? shortDigest(tag.digest) : undefined}
                  >
                    {at('switchToTag')}: <span className="font-mono">{tag.name}</span>
                    {tag.points_to_current && (
                      <span className="ml-1 text-xs text-green-700">
                        ({at('tagPointsToCurrent')})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {!pinned && runningDigest && (
              <div className="mt-3">
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  onClick={() => applyReference(runningDigest)}
                >
                  {at('pinCurrentDigest')}:{' '}
                  <span className="font-mono">{shortDigest(runningDigest)}</span>
                </button>
                <p className="text-xs text-gray-500 mt-1">{at('pinCurrentDigestHint')}</p>
              </div>
            )}
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium mb-1">{at('fieldDeviceTypes')} *</span>
          <div className="space-y-1">
            {activeDeviceTypes.map((dt) => (
              <label key={dt.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={deviceTypeIds.includes(dt.id)}
                  onChange={() => toggleDeviceType(dt.id)}
                />
                <span>{dt.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="edit-app-config">
              {at('fieldConfig')}
            </label>
            <input
              id="edit-app-config"
              type="file"
              accept=".yaml,.yml"
              onChange={(e) => readFile(e.target.files?.[0], setConfigYaml, setConfigName)}
            />
            {configName && (
              <p className="text-gray-600 text-sm mt-1">
                {at('selectedFile')}: {configName}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="edit-app-params">
              {at('fieldParams')}
            </label>
            <input
              id="edit-app-params"
              type="file"
              accept=".yaml,.yml"
              onChange={(e) => readFile(e.target.files?.[0], setParamsYaml, setParamsName)}
            />
            {paramsName && (
              <p className="text-gray-600 text-sm mt-1">
                {at('selectedFile')}: {paramsName}
              </p>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 -mt-3">{at('configKeepHint')}</p>

        {/* ---- What the change costs ---- */}
        {willNeedReapproval && (
          <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4" role="status">
            <h4 className="text-yellow-900 font-medium mb-1">{at('reapprovalTitle')}</h4>
            <p className="text-yellow-800 text-sm">{at('reapprovalBody')}</p>
            {app.environment === 'prod' && (
              <p className="text-yellow-800 text-sm mt-1 font-medium">
                {at('reapprovalProdNote')}
              </p>
            )}
          </div>
        )}

        {submitError && (
          <p className="text-red-600 text-sm" role="alert">
            {submitError}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {at('cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!canSubmit || updateMutation.isPending}
          >
            {updateMutation.isPending ? at('saving') : at('save')}
          </button>
        </div>
        {!hasChanges && <p className="text-xs text-gray-400 text-right">{at('noChanges')}</p>}
      </form>
    </Modal>
  )
}

export default AppEditModal
